/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { findIndex, values, cloneDeep, orderBy } from 'lodash';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import Datepicker from 'react-datepicker';
import Modal from '../../../components/Modal/ReactBootstrapModal';

import DateTimeSelect from './DateTimeSelect';
import LocationSelection from './LocationSelect';
import MatchDndCard from './MatchDnDCard';
import CheckBox from '../../../components/Form/CheckBox';
import DropDownSelect from './DropDownSelect';
import validate from './validate';

import { saveOrderOfPlay } from '../../../actions/draw-edit-actions';
import { addOOPTimeLabel } from '../../../utils/events';
import { drawPositionTypeIds } from '../../../utils/eventConstants';

const _ = { findIndex, values, cloneDeep, orderBy };

const timeRegex = /^([0-1][0-9]|[2][0-3]):([0-5][0-9])$/gm;

const OrderOfPlayModalV2 = (props) => {
    const { closeModals, maxMatchDate, selectedMatchMoment, match: selectedMatch, drawId, oopInfo, timeZone, playersDictByMemberId, teamsDict, ...restProps } = props;

    const { locations, event, orderOfPlay } = useSelector(({ drawEdit }) => {
        const { locations, event, orderOfPlay } = drawEdit;
        return { locations, event, orderOfPlay };
    });
    const dispatch = useDispatch();

    const { eventSchedule: { eventStartUtc, eventEndUtc } } = event.data || {};

    const eventStart = moment(eventStartUtc);
    eventStart.tz(timeZone.value).set({ hour: 0, minute: 0, second: 0 });
    const initialDate = selectedMatch?.date ? moment.utc(selectedMatch?.date) : eventStart;
    const initialTime = selectedMatch?.date && oopInfo?.includesTime ? moment.utc(selectedMatch?.date).tz(timeZone.value).format('HH:mm') : '';
    const initialDateString = initialDate.format();
    const [step, setStep] = useState(orderOfPlay.data?.find((oopObj) => oopObj?.matchId === selectedMatch.id) ? 2 : 1);
    const [date, setDate] = useState(initialDateString);
    const [time, setTime] = useState(initialTime);
    const [notBefore, setNotBefore] = useState(oopInfo?.notBefore);
    const [location, setLocation] = useState('');
    const [facility, setFacility] = useState('');
    const [court, setCourt] = useState('');
    const [selectedCourtOop, setSelectedCourtOop] = useState([]);
    const [facilitiesList, setFacilitiesList] = useState([]);
    const [courtsList, setCourtsList] = useState([]);
    const [matchPositioned, setMatchPositioned] = useState(false);
    const [selectedMatchOopData, setSelectedMatchOopData] = useState({});
    const [locationDisplay, setLocationDisplay] = useState('');
    const [errors, setErrors] = useState(null);
    const [timeUnsetError, setTimeUnsetError] = useState(null);

    const locationsList = _.orderBy(locations.data || [], [l => l.name?.toLowerCase()]);

    // timezone codesandbox for testing conversion https://codesandbox.io/s/moment-timezone-test-forked-pj180
    const handleDateChange = (value) => {
        // input times in local timezone
        if (time && timeRegex.test(time)) {
            const matchTime = time.split(':');
            value.tz(timeZone.value).set({ hour: matchTime[0], minute: matchTime[1], second: 0 });
        } else {
            value.tz(timeZone.value).set({ hour: 0, minute: 0, second: 0 });
        }
        // keep date in local timezone in the state for the inputs
        setDate(moment.utc(value).format());
    };

    const handleTimeChange = (value) => {
        if (value !== '' && value !== 'unset') {
            const newTime = value.split(':');
            const newDateTime = moment(date).tz(timeZone.value);
            newDateTime.set({ hour: newTime[0], minute: newTime[1], second: 0 });
            const matchDateUTC = newDateTime.utc().format();
            setDate(matchDateUTC);
            setTimeUnsetError(false);
        } else {
            // Remove NotBefore if time is not set
            setNotBefore(false);
        }
        setTime(value);
    };

    const handleNotBeforeChange = (value) => {
        if (time === '' || time === 'unset') {
            setTimeUnsetError(true);
        } else {
            setTimeUnsetError(false);
            setNotBefore(value);
        }
    };

    const isNextDisabled = () => {
        return moment(date).tz(timeZone.value).isSameOrAfter(moment.utc(eventEndUtc).tz(timeZone.value), 'day');
    };

    const nextDay = () => {
        if (!isNextDisabled()) {
            const newDate = moment(date).add(1, 'd');
            setDate(newDate);
            setSelectedMatchOopData({
                ...selectedMatchOopData,
                date: newDate,
            });
        }
    };

    const isPrevDisabled = () => {
        return moment(date).tz(timeZone.value).isSameOrBefore(moment.utc(eventStartUtc).tz(timeZone.value), 'day');
    };

    const prevDay = () => {
        if (!isPrevDisabled()) {
            const newDate = moment(date).subtract(1, 'd');
            setDate(newDate);
            setSelectedMatchOopData({
                ...selectedMatchOopData,
                date: newDate,
            });
        }
    };

    const findMatch = (matchId) => {
        if (selectedMatch.id === matchId) {
            return selectedMatch;
        }
        const foundMatch = (orderOfPlay.matches || []).find(match => match.id === matchId);
        return foundMatch;
    };

    const addMatchToOOPObject = (oopObj) => {
        const match = findMatch(oopObj.matchId);
        oopObj.date = match?.date;
        return oopObj;
    };

    useEffect(() => {
        const matchAsOopObj = {
            matchId: selectedMatch.id,
            date: selectedMatch.date || date,
            includesTime: selectedMatch.includesTime,
            notBefore: selectedMatch.notBefore,
            locationId: location,
            facilityId: facility,
            courtId: court,
        };
        setSelectedMatchOopData(matchAsOopObj);
    }, []);

    useEffect(() => {
        let courtOrderOfPlay = orderOfPlay.data?.map(oopObj => {
            return addMatchToOOPObject(oopObj);
        });
        courtOrderOfPlay = orderOfPlay.data?.filter(match => {
            const isSameDay = moment.utc(match.date).tz(timeZone.value).format('YYYYMMDD') === moment(date).tz(timeZone.value).format('YYYYMMDD');
            return isSameDay && court === match.courtId;
        })
            .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
        courtOrderOfPlay = addOOPTimeLabel(courtOrderOfPlay, timeZone);
        // if match positioned on the current court & date
        if (courtOrderOfPlay.find((oopObj) => oopObj?.matchId === selectedMatch.id)) {
            setMatchPositioned(true);
        } else {
            setMatchPositioned(false);
        }

        setSelectedCourtOop(courtOrderOfPlay);
    }, [court, orderOfPlay.data, date]);

    useEffect(() => {
        if (oopInfo && oopInfo.locationId && oopInfo.facilityId && oopInfo.courtId) {
            setLocation(oopInfo.locationId);
            if (locations) {
                const selectedLocation = locations.data.find(({ id }) => id === oopInfo.locationId);
                setFacilitiesList(selectedLocation.facilities);
                setFacility(oopInfo.facilityId);
                const selectedFacility = selectedLocation.facilities.find(({ id }) => id === oopInfo.facilityId);
                setCourtsList(_.orderBy(selectedFacility.courts, [c => c.name?.toLowerCase()]));
                setCourt(oopInfo.courtId);
                const selectedCourt = selectedFacility.courts.find(({ id }) => id === oopInfo.courtId);
                setLocationDisplay(`${selectedLocation.name}, ${selectedFacility.abbreviation || selectedFacility.name}, ${selectedCourt.name}`);
            }
        }
    }, [oopInfo, locations]);

    const updateSequenceNumbers = (targetClone) => {
        targetClone.forEach((match, i) => {
            match.sequenceNumber = i;
        });
        return targetClone;
    };

    const handleNextClick = () => {
        const updatedOopData = { ...selectedMatchOopData };
        updatedOopData.date = date;
        updatedOopData.notBefore = notBefore;
        updatedOopData.includesTime = time !== '' && time !== 'unset';
        setSelectedMatchOopData(updatedOopData);
        if (matchPositioned) {
            const matchIndex = _.findIndex(selectedCourtOop, match => match.matchId === selectedMatch.id);
            let targetClone = _.cloneDeep(selectedCourtOop);
            if (targetClone[matchIndex]) {
                targetClone[matchIndex].date = date;
                targetClone[matchIndex].includesTime = time !== '' && time !== 'unset';
                targetClone[matchIndex].notBefore = notBefore;
                addOOPTimeLabel(targetClone, timeZone);
                targetClone = updateSequenceNumbers(targetClone);
                setErrors(validate(targetClone));
                setSelectedCourtOop(targetClone);
            }
        }
        setStep(2);
    };

    const handleLocationChange = (e) => {
        setLocation(e.target.value);
        const selectedLocation = locationsList.find(({ id }) => id === e.target.value);
        if (selectedLocation) {
            setFacility('');
            setFacilitiesList(_.orderBy(selectedLocation.facilities, [f => f.name?.toLowerCase()]));
            setCourt('');
            setCourtsList([]);
        }
    };
    const handleFacilityChange = (e) => {
        setFacility(e.target.value);
        const selectedFacility = facilitiesList.find(({ id }) => id === e.target.value);
        if (selectedFacility) {
            setCourt('');
            setCourtsList(_.orderBy(selectedFacility.courts, [c => c.name?.toLowerCase()]));
        }
    };

    const updateOrderOfPlay = () => {
        const updatePayload = {
            courtId: court,
            matches: selectedCourtOop.map((match, index) => {
                return {
                    matchId: match.matchId,
                    date: match.date,
                    dateTimeUTC: match.date,
                    notBefore: match.notBefore,
                    includesTime: match.includesTime,
                    sequenceNumber: index,
                    courtId: court,
                    facilityId: facility,
                    locationId: location,
                };
            }),
        };
        dispatch(saveOrderOfPlay(event.data.id, selectedMatch.id, updatePayload));
        closeModals();
    };

    const onDragEnd = (result) => {
        const { source, destination } = result;

        if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) {
            return;
        }
        let targetClone = _.cloneDeep(selectedCourtOop);

        // being dragged from the top section
        if (source.droppableId !== destination.droppableId) {
            targetClone.splice(destination.index, 0, selectedMatchOopData);
            addOOPTimeLabel(targetClone, timeZone);
            setSelectedCourtOop(targetClone);
            targetClone = updateSequenceNumbers(targetClone);
            setErrors(validate(targetClone));
            setMatchPositioned(true);
        } else if (source.droppableId === destination.droppableId) {
            const targetMatch = targetClone[source.index];
            targetClone.splice(source.index, 1);
            targetClone.splice(destination.index, 0, targetMatch);
            addOOPTimeLabel(targetClone, timeZone);
            targetClone = updateSequenceNumbers(targetClone);
            setErrors(validate(targetClone));
            setSelectedCourtOop(targetClone);
        }
    };

    const formatMatchDetails = (match) => {
        const matchDetails = {
            ...match,
        };

        if (match?.position1?.typeId === drawPositionTypeIds.PLAYER && match.position2?.typeId === drawPositionTypeIds.PLAYER) {
            const playerOne = playersDictByMemberId[match.position1.memberId];
            const playerTwo = playersDictByMemberId[match.position2.memberId];

            matchDetails.position1.name = `${playerOne?.firstName} ${playerOne?.lastName}`;
            matchDetails.position2.name = `${playerTwo?.firstName} ${playerTwo?.lastName}`;
        }

        if (match?.position1?.typeId === drawPositionTypeIds.TEAM) {
            const teamOne = teamsDict[match.position1.teamId];
            matchDetails.position1.name = teamOne?.displayName;
        }

        if (match?.position2?.typeId === drawPositionTypeIds.TEAM) {
            const teamTwo = teamsDict[match.position2.teamId];
            matchDetails.position2.name = teamTwo?.displayName;
        }

        return matchDetails;
    };

    return (
        <Modal visible={true} onClickBackdrop={closeModals}>
            <div className="modal-header">
                <div className="text-medium">Order of Play</div>
                <div className="close-icon" onClick={closeModals}><i className="utr utr-close" /></div>
            </div>
            <div className="modal-body">
                {step === 1 ? (
                    <>
                        <h4>Select Court</h4>
                        <div className={`form-group form-group-xl`}>
                            <label>Date &amp; Time</label>
                            <DateTimeSelect
                                onDateChange={handleDateChange}
                                onTimeChange={handleTimeChange}
                                matchDateMoment={moment(date).tz(timeZone.value)}
                                matchTime={time}
                                minDate={moment.utc(eventStartUtc).tz(timeZone.value)}
                                maxDate={moment.utc(eventEndUtc).tz(timeZone.value)}
                            />
                            {timeUnsetError && (
                                <div className="error-text col-5 offset-7 pl4">Please select time.</div>
                            )}
                            <div className="row mt16 mb16">
                                <div className="col-12">
                                    <CheckBox
                                        onChange={value => handleNotBeforeChange(value)}
                                        customLabel={'Not Before'}
                                        id="notBefore"
                                        controlled={true}
                                        checked={notBefore}
                                        formGroupCssClass={'mb0'}
                                    />
                                </div>
                            </div>
                            <div>All matches without a start time will be considered following the previous match</div>
                        </div>
                        <LocationSelection
                            loading={locations.loading}
                            location={location}
                            facility={facility}
                            court={court}
                            locations={locationsList}
                            facilities={facilitiesList}
                            courts={courtsList}
                            onLocationChange={handleLocationChange}
                            onFacilityChange={handleFacilityChange}
                            onCourtChange={e => setCourt(e.target.value)}
                        />
                        <button disabled={!court} type="button" className={`btn btn-xl btn-block btn-primary`} onClick={handleNextClick}>Next</button>
                    </>
                ) : (
                    <>
                        <DropDownSelect
                            label={'Select Court'}
                            wrapperClass={'mb16'}
                            value={court}
                            onChange={e => setCourt(e.target.value)}
                            options={courtsList}
                            defaultOption={{ value: '', text: 'Choose court...' }}
                            loading={locations.loading}
                        />
                        <div className="row">
                            <div className="col-6 offset-md-6 d-flex">
                                <div className={`click-area click-area-36 float-left ${isPrevDisabled() ? 'disabled' : ''}`} onClick={prevDay}><i className="uts uts-prev" /></div>
                                <Datepicker
                                    className="form-control form-control-sm mb16"
                                    selected={moment(date).tz(timeZone.value)}
                                    onChange={handleDateChange}
                                    minDate={moment.utc(eventStartUtc).tz(timeZone.value)}
                                    maxDate={moment.utc(eventEndUtc).tz(timeZone.value)}
                                />
                                <div className={`click-area click-area-36 float-right ${isNextDisabled() ? 'disabled' : ''}`} onClick={nextDay}><i className="uts uts-next" /></div>
                            </div>
                        </div>
                        {(oopInfo && oopInfo.courtId !== court) && (
                            <span className="error-text">This match is currently scheduled on {locationDisplay}. Dragging into another order of play will remove it from the current one.</span>
                        )}
                        {errors && (
                            <span className="error-text">This order of play sequence is invalid, matches must not precede matches that are schedule at a later time or be scheduled for the same time. Please correct to continue.</span>
                        )}
                        <DragDropContext
                            onDragEnd={onDragEnd}
                        >
                            <Droppable
                                droppableId={'match-dnd-source'}
                                isDropDisabled={true}
                            >
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className={`match-dnd-source`}
                                        style={{ minHeight: !matchPositioned ? '50px' : '0px' }}
                                    >
                                        {!matchPositioned ? (
                                            <MatchDndCard
                                                match={{
                                                    ...selectedMatch,
                                                    ...selectedMatchOopData,
                                                }}
                                                matchDetails={formatMatchDetails(selectedMatch)}
                                                index={0}
                                                timeZone={timeZone}
                                                isSourceCard={true}
                                                {...restProps}
                                            />
                                        ) : <></>}
                                    </div>
                                )}
                            </Droppable>
                            <label className="text-teal">Court Schedule</label>
                            <Droppable
                                droppableId={'match-dnd-target'}
                            >
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className={`match-dnd-target ${!selectedCourtOop.length ? 'empty' : ''}`}
                                        style={{ minHeight: '50px' }}
                                    >
                                        {selectedCourtOop.map((oopObj, i) => {
                                            const foundMatch = findMatch(oopObj.matchId);

                                            if (foundMatch) {
                                                return (
                                                    <MatchDndCard
                                                        key={oopObj.matchId}
                                                        match={oopObj}
                                                        selectedMatch={foundMatch}
                                                        matchDetails={formatMatchDetails(foundMatch)}
                                                        index={i}
                                                        timeZone={timeZone}
                                                        currentMatch={oopObj.matchId === selectedMatch.id}
                                                        {...restProps}
                                                    />
                                                );
                                            }
                                            return <div>Not found</div>;
                                        })}
                                        {provided.placeholder}
                                        {!selectedCourtOop.length && (
                                            <div className="placeholder">Drop card here...</div>
                                        )}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                        <div className="row">
                            <div className={'col-6'}>
                                <button disabled={court === null} type="button" className={`btn btn-xl btn-block btn-default`} onClick={() => setStep(1)}>Back</button>
                            </div>
                            <div className={'col-6'}>
                                <button disabled={!matchPositioned || errors} type="button" className={`btn btn-xl btn-block btn-primary`} onClick={updateOrderOfPlay}>OK</button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default OrderOfPlayModalV2;
